import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Loader2, RotateCcw } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import useAuthStore from '../../../../stores/authStore';
import { authenticatedFetch } from '../../../../utils/apiClient';
import './ExternalAppReauth.css';

type ExternalAppConfig = {
  appSlug: string;
  label: string;
  buildSsoEndpoint: (returnPath: string) => string;
};

type StatusKind = 'loading' | 'denied' | 'error';

const EXTERNAL_APPS: Record<string, ExternalAppConfig> = {
  shopvista: {
    appSlug: 'shopvista',
    label: 'ShopVista 门店经营',
    buildSsoEndpoint: (returnPath) =>
      `/api/shopvista/sso-link?return_path=${encodeURIComponent(returnPath)}`,
  },
  cmi: {
    appSlug: 'cmi',
    label: '用户与市场洞察',
    buildSsoEndpoint: (returnPath) =>
      `/api/cmi/sso-link?return_path=${encodeURIComponent(returnPath)}`,
  },
  sonar: {
    appSlug: 'sonar',
    label: 'Sonar 售后分析',
    buildSsoEndpoint: (returnPath) =>
      `/api/sonar/sso-link?return_path=${encodeURIComponent(returnPath)}`,
  },
  parallax: {
    appSlug: 'parallax',
    label: 'Parallax 推广视差',
    buildSsoEndpoint: (returnPath) =>
      `/api/parallax/sso-link?return_path=${encodeURIComponent(returnPath)}`,
  },
  prism: {
    appSlug: 'prism',
    label: 'Prism 运营棱镜',
    buildSsoEndpoint: (returnPath) =>
      `/api/prism/sso-link?return_path=${encodeURIComponent(returnPath)}`,
  },
  'core-daily-report': {
    appSlug: 'core-daily-report',
    label: 'Core Daily Report 核心日报',
    buildSsoEndpoint: (returnPath) =>
      `/api/core-daily-report/sso-link?return_path=${encodeURIComponent(returnPath)}`,
  },
  'marketing-workflow': {
    appSlug: 'marketing-workflow',
    label: 'Marketing Workflow 营销工作流',
    buildSsoEndpoint: (returnPath) =>
      `/api/marketing-workflow/sso-link?return_path=${encodeURIComponent(returnPath)}`,
  },
  'ads-ga4-roi': {
    appSlug: 'ads-ga4-roi',
    label: 'Ads × GA4 ROI 快照',
    buildSsoEndpoint: (returnPath) =>
      `/api/ads-ga4-roi/sso-link?return_path=${encodeURIComponent(returnPath)}`,
  },
  'points-risk-control': {
    appSlug: 'points-risk-control',
    label: 'Points Risk Control',
    buildSsoEndpoint: (returnPath) =>
      `/api/data-science-lab/sso-link?project=points-risk-control&return_path=${encodeURIComponent(returnPath)}`,
  },
};

function sanitizeReturnPath(pathValue: string | null): string {
  if (!pathValue || !pathValue.startsWith('/') || pathValue.startsWith('//') || pathValue.startsWith('/\\')) {
    return '/';
  }
  return pathValue;
}

function getFailureMessage(status: number, reason: string | null, appLabel: string): string {
  if (status === 403) {
    return `当前账号没有访问 ${appLabel} 的权限。`;
  }
  if (status === 503) {
    return `${appLabel} 暂时不可用，请稍后重试。`;
  }
  return reason || `暂时无法打开 ${appLabel}。`;
}

export default function ExternalAppReauth() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const appSlug = params.get('app') ?? '';
  const returnPath = sanitizeReturnPath(params.get('return'));
  const appConfig = EXTERNAL_APPS[appSlug];

  const isInitialized = useAuthStore(s => s.isInitialized);
  const accessToken = useAuthStore(s => s.accessToken);
  const [message, setMessage] = useState<string | null>(null);
  const [statusLabel, setStatusLabel] = useState('正在确认访问权限');
  const [statusKind, setStatusKind] = useState<StatusKind>('loading');

  const currentRoute = useMemo(() => {
    const appParam = encodeURIComponent(appSlug);
    const returnParam = encodeURIComponent(returnPath);
    return `/auth/external-app-reauth?app=${appParam}&return=${returnParam}`;
  }, [appSlug, returnPath]);

  useEffect(() => {
    if (!isInitialized) return;

    if (!appConfig) {
      setStatusKind('error');
      setStatusLabel('外部应用不可用');
      setMessage('这个外部应用尚未完成接入配置。');
      return;
    }

    if (!accessToken) {
      navigate(`/login?next=${encodeURIComponent(currentRoute)}`, { replace: true });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setStatusKind('loading');
        setStatusLabel(`正在打开 ${appConfig.label}`);
        setMessage(null);
        const response = await authenticatedFetch(appConfig.buildSsoEndpoint(returnPath), {
          loginRedirectPath: currentRoute,
        });
        if (cancelled) return;

        if (!response.ok) {
          let reason: string | null = null;
          try {
            const body = await response.json();
            reason = body?.detail || body?.message || body?.error || null;
          } catch {
            reason = null;
          }
          setStatusKind(response.status === 403 ? 'denied' : 'error');
          setStatusLabel(response.status === 403 ? '暂无访问权限' : '无法打开应用');
          setMessage(getFailureMessage(response.status, reason, appConfig.label));
          return;
        }

        const data = await response.json();
        if (data?.url) {
          window.location.replace(data.url);
          return;
        }

        setStatusKind('error');
        setStatusLabel('无法打开应用');
        setMessage(`暂时没有拿到 ${appConfig.label} 的登录地址。`);
      } catch (err) {
        if (!cancelled) {
          setStatusKind('error');
          setStatusLabel('无法打开应用');
          setMessage(err instanceof Error ? err.message : `暂时无法打开 ${appConfig.label}。`);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accessToken, appConfig, currentRoute, isInitialized, navigate, returnPath]);

  const targetLabel = appConfig?.label ?? '外部应用';
  const detailText = message || (isInitialized ? '正在确认你的访问权限。' : '正在加载登录状态。');
  const isLoading = statusKind === 'loading';

  return (
    <main className="external-reauth-page">
      <section className={`external-reauth-panel external-reauth-panel--${statusKind}`} aria-live="polite">
        <div className="external-reauth-brand">
          <div className="external-reauth-logo" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
          <span className="external-reauth-brand-name">数据中心</span>
          <span className="external-reauth-route-line" aria-hidden="true" />
          <span className="external-reauth-target" aria-label={`目标应用：${targetLabel}`}>
            {targetLabel}
          </span>
        </div>

        <div className={`external-reauth-status${isLoading ? ' external-reauth-status--with-icon' : ''}`}>
          {isLoading && (
            <div className="external-reauth-status-icon" aria-hidden="true">
              <Loader2 size={22} strokeWidth={2} />
            </div>
          )}
          <div className="external-reauth-status-copy">
            <h1>{statusLabel}</h1>
            <p>{detailText}</p>
          </div>
        </div>

        {message && (
          <div className="external-reauth-actions">
            <button
              type="button"
              className="external-reauth-button external-reauth-button--primary"
              onClick={() => navigate('/', { replace: true })}
            >
              <ArrowLeft size={16} />
              返回控制台
            </button>
            <button
              type="button"
              className="external-reauth-button external-reauth-button--secondary"
              onClick={() => window.location.reload()}
            >
              <RotateCcw size={16} />
              重试
            </button>
          </div>
        )}

        {!message && (
          <div className="external-reauth-progress" aria-hidden="true">
            <span />
          </div>
        )}
      </section>
    </main>
  );
}
